<script setup>
// Skład wpisu posiłku: lista snapshotów (nazwa, waga, makra) z możliwością
// zmiany wagi i usuwania. Nowy składnik bierze makra z produktu (per 100 g);
// nazwa spoza katalogu daje pozycję bez makr — API na to pozwala. Produkt z wagą
// porcji pozwala wpisać ilość w sztukach; do wpisu i tak idą gramy.
import { computed, ref } from 'vue';
import { formatMoney } from '@/composables/useCurrency.js';
import ProductPicker from '@/components/v2/product-picker.vue';
import BarcodeScan from '@/components/v2/barcode-scan.vue';
import AmountInput from '@/components/v2/amount-input.vue';
import { useV2Entry } from '@/composables/useV2Entry.js';
import { useApi } from '~/composables/useApi.ts';
import { useBarcode } from '@/composables/useBarcode.ts';
import { norm } from '@/composables/useV2Text.js';
import { pickedWeight } from '@/composables/usePortions.js';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    ingredients: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
});
const emit = defineEmits(['update']);

const { ingredientFromProduct, rescaleIngredient, sumIngredients } = useV2Entry();
const { t } = useI18n();
const api = useApi();
const { lookup } = useBarcode();

const newName = ref('');
const newProduct = ref(null);
const newWeight = ref('100');
const addError = ref(null);
const picker = ref(null);
const scanning = ref(false);
const scanBusy = ref(false);

const totals = computed(() => ({
    ...sumIngredients(props.ingredients),
    weight: props.ingredients.reduce((a, i) => a + (i.weight ?? 0), 0),
}));

const productByName = (name) => {
    const norm = (name ?? '').trim().toLowerCase();
    return props.products.find(p => (p.name ?? '').trim().toLowerCase() === norm) ?? null;
};

// Produkt nowej pozycji: wybrany z listy albo trafiony wpisaną nazwą.
const newItemProduct = computed(() => newProduct.value ?? productByName(newName.value));

const add = () => {
    addError.value = null;
    const name = newName.value.trim();
    const weight = Number(newWeight.value);

    if (!name) { addError.value = t('entry.ingredients.nameRequired'); return; }
    if (!weight || weight <= 0) { addError.value = t('entry.ingredients.weightPositive'); return; }

    // Wybór z listy ma pierwszeństwo; wpisana nazwa i tak jest sprawdzana w katalogu.
    const product = newProduct.value ?? productByName(name);
    const ingredient = product
        ? ingredientFromProduct(product, weight)
        : { product_name: name, weight: Math.round(weight) };

    emit('update', [...props.ingredients, ingredient]);
    newName.value = '';
    newProduct.value = null;
    newWeight.value = '100';
    picker.value?.focus();
};

// Skan kodu: produkt z katalogu wchodzi wprost, a nieznany dociągamy z Open Food
// Facts i zapisujemy w katalogu — wtedy wpis dostaje pełne makra, a produkt zostaje
// na przyszłość (kolejny skan trafi już w niego).
const onScanned = async (code) => {
    scanning.value = false;
    addError.value = null;
    // Przy domyślnej wadze produkt liczony w sztukach wchodzi jako 1 sztuka.
    const weight = (product) => pickedWeight(product, newWeight.value);

    const byCode = props.products.find(p => p.external_id && String(p.external_id) === String(code));
    if (byCode) { pushIngredient(ingredientFromProduct(byCode, weight(byCode))); return; }

    scanBusy.value = true;
    try {
        const found = await lookup(code);
        if (!found) {
            addError.value = t('entry.ingredients.codeNotFound', { code });
            return;
        }

        const byName = props.products.find(p => norm(p.name) === norm(found.name));
        if (byName) { pushIngredient(ingredientFromProduct(byName, weight(byName))); return; }

        const created = await api.post('/products', {
            name: found.name,
            calories: found.calories ?? null,
            protein: found.protein ?? null,
            fat: found.fat ?? null,
            carbs: found.carbs ?? null,
            sugar: found.sugar ?? null,
            fiber: found.fiber ?? null,
            saturated_fat: found.saturated_fat ?? null,
            salt: found.salt ?? null,
            source: 'openfoodfacts',
            external_id: code,
        });
        pushIngredient(ingredientFromProduct(created, weight(created)));
    } catch {
        addError.value = t('entry.ingredients.codeCheckFailed');
    } finally {
        scanBusy.value = false;
    }
};

const pushIngredient = (ingredient) => {
    emit('update', [...props.ingredients, ingredient]);
    newName.value = '';
    newProduct.value = null;
    newWeight.value = '100';
};

const changeWeight = (index, weight) => {
    if (!weight || weight <= 0 || weight === props.ingredients[index]?.weight) return;
    const next = [...props.ingredients];
    next[index] = rescaleIngredient(next[index], weight);
    emit('update', next);
};

const remove = (index) => {
    emit('update', props.ingredients.filter((_, i) => i !== index));
};
</script>

<template>
    <div>
        <span class="text-[9px] font-bold tracking-[0.18em] text-[#A29A8E] block mb-2">{{ t('entry.ingredients.heading') }}</span>

        <ul v-if="ingredients.length" class="flex flex-col mb-2">
            <li
                v-for="(ing, i) in ingredients"
                :key="`${ing.product_name}-${i}`"
                class="flex items-center gap-2 py-1 border-b border-[#F1ECE3] group/ing"
            >
                <span class="flex-1 min-w-0 truncate text-[13px] text-[#211E1A]">{{ ing.product_name }}</span>

                <label class="sr-only" :for="`v2-ing-${i}`">{{ t('entry.ingredients.weightOf', { name: ing.product_name }) }}</label>
                <AmountInput
                    :id="`v2-ing-${i}`"
                    :model-value="ing.weight"
                    :product="productByName(ing.product_name)"
                    compact
                    @commit="w => changeWeight(i, w)"
                />

                <span class="text-[12px] tabular-nums text-[#A29A8E] w-[68px] text-right">
                    {{ ing.calories ?? 0 }} kcal
                </span>

                <button
                    type="button"
                    :title="t('entry.ingredients.remove', { name: ing.product_name })"
                    class="w-6 h-6 rounded flex items-center justify-center text-[#B8B0A3] opacity-100 lg:opacity-0 lg:group-hover/ing:opacity-100 focus-visible:opacity-100 hover:text-[#B5643C] hover:bg-[#F9F0EA] transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C0764B]"
                    @click="remove(i)"
                >
                    <i class="pi pi-times text-[10px]"></i>
                </button>
            </li>
        </ul>
        <p v-else class="text-[12.5px] text-[#A29A8E] mb-2">{{ t('entry.ingredients.empty') }}</p>

        <div class="flex items-end gap-2">
            <ProductPicker
                ref="picker"
                v-model="newName"
                v-model:product="newProduct"
                :products="products"
                allow-free
                id="v2-ing-name"
                :label="t('entry.ingredients.ingredient')"
                :placeholder="t('entry.ingredients.searchPlaceholder')"
                class="flex-1 min-w-0"
                @submit="add"
            />
            <div :class="newItemProduct?.portion_weight ? 'w-[148px]' : 'w-[92px]'">
                <label class="v2-label" for="v2-ing-weight">{{ t('entry.ingredients.amount') }}</label>
                <AmountInput
                    id="v2-ing-weight"
                    v-model="newWeight"
                    :product="newItemProduct"
                    reset-on-product
                    @enter="add"
                />
            </div>
            <button
                type="button"
                :title="t('entry.ingredients.scan')"
                class="w-[38px] h-[38px] shrink-0 rounded-[10px] border border-[#E4DDD1] flex items-center justify-center text-[#4A443C] hover:border-[#211E1A] hover:text-[#211E1A] disabled:opacity-60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]"
                :disabled="scanBusy"
                @click="scanning = true"
            >
                <i class="pi pi-qrcode text-[13px]"></i>
            </button>
            <button
                type="button"
                class="h-[38px] px-3 rounded-[10px] border border-[#E4DDD1] text-[12.5px] font-semibold text-[#4A443C] hover:border-[#211E1A] hover:text-[#211E1A] disabled:opacity-60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]"
                :disabled="scanBusy"
                @click="add"
            >
                {{ t('entry.ingredients.add') }}
            </button>
        </div>
        <p v-if="scanBusy" class="text-[11.5px] text-[#8A8378] mt-1">{{ t('entry.ingredients.checkingCode') }}</p>
        <p v-else-if="addError" class="text-[11.5px] text-[#B5643C] mt-1">{{ addError }}</p>
        <p v-else class="text-[11px] text-[#B8B0A3] mt-1">
            {{ t('entry.ingredients.hint') }}
        </p>

        <BarcodeScan :show="scanning" @close="scanning = false" @detected="onScanned" />

        <p v-if="ingredients.length" class="text-[11.5px] text-[#8A8378] mt-2 tabular-nums">
            {{ t('entry.ingredients.total', { kcal: totals.calories, grams: totals.weight }) }}<template v-if="totals.price"> · {{ formatMoney(totals.price / 100) }}</template>
        </p>
    </div>
</template>
